// src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/auth';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isSystemAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  canEdit: () => boolean;  // SystemAdmin or CompanyAdmin (not CompanyUser)
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      // Restore user from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        // Fallback: try to parse from JWT (shouldn't normally happen)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000;
          if (Date.now() > exp) {
            // Token expired
            logout();
          }
        } catch {
          logout();
        }
      }
    }
    setIsLoading(false);
  }, [token]);

  const login = async (username: string, password: string) => {
    const response = await authService.login(username, password);

    const newUser: User = {
      username: response.username,
      displayName: response.displayName,
      role: response.role,
      companyId: response.companyId,
      companyName: response.companyName
    };

    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(response.token);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const isSystemAdmin = () => user?.role === 'SystemAdmin';
  const isCompanyAdmin = () => user?.role === 'CompanyAdmin';
  const canEdit = () => user?.role === 'SystemAdmin' || user?.role === 'CompanyAdmin';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isLoading,
      login,
      logout,
      isSystemAdmin,
      isCompanyAdmin,
      canEdit
    }}>
      {children}
    </AuthContext.Provider>
  );
};
